import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Target, AlertTriangle, Check, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface MatchResponse {
	job: {
		id: string;
		title: string;
		total_skills: number;
		total_experiences: number;
	};
	candidate: {
		id: string;
		name: string;
		function: string;
		total_skills: number;
		total_experiences: number;
	};
	match_summary: {
		overall_coverage: number;
		skill_coverage: number;
		experience_coverage: number;
		similarity_threshold: number;
		skills_matched: number;
		skills_missing: number;
		experiences_matched: number;
		experiences_missing: number;
	};
	shared: {
		skills: Array<{
			job_skill: {
				id: string;
				name: string;
				is_soft_skill: boolean;
			};
			candidate_skill: {
				id: string;
				name: string;
				raw_name: string;
			};
			similarity: number;
			match_type: 'high' | 'medium' | 'low';
		}>;
		experiences: Array<{
			job_experience: {
				id: string;
				name: string;
				category: string;
			};
			candidate_experience: {
				id: string;
				name: string;
				category: string;
				raw_text: string;
				start_date: string | null;
				end_date: string | null;
			};
			similarity: number;
			match_type: 'high' | 'medium' | 'low';
		}>;
	};
	missing: {
		skills: Array<{
			id: string;
			name: string;
			is_soft_skill: boolean;
		}>;
		experiences: Array<{
			id: string;
			name: string;
			category: string;
		}>;
	};
	extra: {
		skills: Array<{
			id: string;
			name: string;
			raw_name: string;
		}>;
		experiences: Array<{
			id: string;
			name: string;
			category: string;
			raw_text: string;
			start_date: string | null;
			end_date: string | null;
		}>;
	};
}

interface SkillsComparisonGraphProps {
	matchId: string;
	jobId: string;
	candidateId: string;
	onMissingSkills?: (skills: string[]) => void;
	onLoadingChange?: (loading: boolean) => void;
	onCandidateProfileUpdated?: () => void;
}

export const SkillsComparisonGraph = ({ matchId, jobId, candidateId, onMissingSkills, onLoadingChange, onCandidateProfileUpdated }: SkillsComparisonGraphProps) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(true);
	const [matchData, setMatchData] = useState<MatchResponse | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [addingItems, setAddingItems] = useState<Set<string>>(new Set());
	const [addedItems, setAddedItems] = useState<Set<string>>(new Set());

	const fetchSkillsComparison = useCallback(async () => {
		if (!jobId || !candidateId) return;

		try {
			setLoading(true);
			setError(null);
			if (onLoadingChange) onLoadingChange(true);

			const { data, error: invokeError } = await supabase.functions.invoke(
				"digger-intelligence?command=match-specific-job-with-specific-candidate-fast&jobId=" + jobId + "&candidateId=" + candidateId,
				{
                    method: 'GET',
				}
			);

			if (invokeError) throw invokeError;

			if (data?.job && data?.candidate) {
				setMatchData(data);

				if (onMissingSkills && data.missing?.skills) {
					const missingSkillNames = data.missing.skills.map((s: any) => s.name);
					onMissingSkills(missingSkillNames);
				}
			} else {
				throw new Error('No result data returned from API');
			}
		} catch (err: any) {
			console.error("Error fetching skills comparison:", err);
			setError(err.message || 'Failed to load skills comparison');
			toast({
				title: t('common.error'),
				description: err.message || "Failed to load skills comparison",
				variant: "destructive",
			});
		} finally {
			setLoading(false);
			if (onLoadingChange) onLoadingChange(false);
		}
	}, [jobId, candidateId, t, onLoadingChange, onMissingSkills]);

	useEffect(() => {
		if (onMissingSkills) onMissingSkills([]);
		if (onLoadingChange) onLoadingChange(false);
		setMatchData(null);
		setError(null);
		setAddingItems(new Set());
		setAddedItems(new Set());
	}, [jobId, candidateId, onMissingSkills, onLoadingChange]);

	useEffect(() => {
		if (jobId && candidateId) {
			fetchSkillsComparison();
		}
	}, [jobId, candidateId, fetchSkillsComparison]);

	const notifyFastVectorService = (id: string) => {
		supabase.functions.invoke(
			'digger-intelligence?command=notify-candidate',
			{ method: 'POST', body: { candidateId: id } }
		).catch(() => {});
	};

	const handleAddSkill = async (skill: { id: string; name: string; is_soft_skill: boolean }) => {
		const key = `skill-${skill.id}`;
		if (addingItems.has(key) || addedItems.has(key)) return;

		setAddingItems(prev => new Set(prev).add(key));

		try {
			const { error: insertError } = await supabase
				.from('candidate_skills')
				.insert({ candidate_id: candidateId, raw_skill_name: skill.name, normalized_skill_id: skill.id });

			if (insertError) throw insertError;

			const { data: candidateData } = await supabase
				.from('candidates')
				.select('skills')
				.eq('id', candidateId)
				.single();

			const currentSkills: string[] = candidateData?.skills || [];
			const updatedSkills = currentSkills.includes(skill.name)
				? currentSkills
				: [...currentSkills, skill.name];

			const { error: updateError } = await supabase
				.from('candidates')
				.update({ skills: updatedSkills, ai_last_updated: new Date().toISOString() })
				.eq('id', candidateId);

			if (updateError) throw updateError;

			setAddedItems(prev => new Set(prev).add(key));
			onCandidateProfileUpdated?.();
			notifyFastVectorService(candidateId);

			toast({
				title: t('common.success'),
				description: t('matching.skillsComparison.added'),
			});
		} catch (err: any) {
			toast({
				title: t('common.error'),
				description: t('matching.skillsComparison.addError'),
				variant: "destructive",
			});
		} finally {
			setAddingItems(prev => {
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
		}
	};

	const handleAddExperience = async (exp: { id: string; name: string; category: string }) => {
		const key = `exp-${exp.id}`;
		if (addingItems.has(key) || addedItems.has(key)) return;

		setAddingItems(prev => new Set(prev).add(key));

		try {
			const { error: insertError } = await supabase
				.from('candidate_experiences')
				.insert({ candidate_id: candidateId, raw_text: exp.name, normalized_experience_id: exp.id, metadata: { category: exp.category } });

			if (insertError) throw insertError;

			const { error: updateError } = await supabase
				.from('candidates')
				.update({ ai_last_updated: new Date().toISOString() })
				.eq('id', candidateId);

			if (updateError) throw updateError;

			setAddedItems(prev => new Set(prev).add(key));
			onCandidateProfileUpdated?.();
			notifyFastVectorService(candidateId);

			toast({
				title: t('common.success'),
				description: t('matching.skillsComparison.added'),
			});
		} catch (err: any) {
			toast({
				title: t('common.error'),
				description: t('matching.skillsComparison.addError'),
				variant: "destructive",
			});
		} finally {
			setAddingItems(prev => {
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
		}
	};

	const getMatchTypeColor = (matchType: 'high' | 'medium' | 'low') => {
		switch (matchType) {
			case 'high':
				return "bg-green-50 text-green-800 border-green-200";
			case 'medium':
				return "bg-yellow-50 text-yellow-800 border-yellow-200";
			case 'low':
				return "bg-orange-50 text-orange-800 border-orange-200";
			default:
				return "bg-gray-50 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700";
		}
	};

	if (loading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Target className="h-5 w-5" />
						Skills & Experience Match
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex justify-center items-center p-8">
						<Loader2 className="h-8 w-8 animate-spin text-gray-500 dark:text-gray-400" />
					</div>
				</CardContent>
			</Card>
		);
	}

	if (error || !matchData) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Target className="h-5 w-5" />
						Skills & Experience Match
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex flex-col items-center justify-center p-8 text-center">
						<AlertTriangle className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
						<p className="text-gray-500 dark:text-gray-400">{error || 'No comparison data available'}</p>
						<Button
							variant="outline"
							onClick={fetchSkillsComparison}
							className="mt-4"
						>
							Retry
						</Button>
					</div>
				</CardContent>
			</Card>
		);
	}

	const { match_summary, shared, missing, extra } = matchData;

	return (
		<div className="space-y-4">
			{/* Not detected in profile - Skills and Experiences side by side */}
			{(missing.skills.length > 0 || missing.experiences.length > 0) && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{missing.skills.length > 0 && (
						<div className="space-y-3">
							<div className="flex items-center gap-2">
								<AlertTriangle className="h-4 w-4 text-gray-500 dark:text-gray-400" />
								<h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
									{t('matching.skillsComparison.notInProfileSkills', { count: missing.skills.length })}
								</h3>
							</div>
							<p className="text-xs text-gray-500 dark:text-gray-400 italic">
								{t('matching.skillsComparison.notInProfileHint')}
							</p>
							<div className="rounded-sm p-3 border border-gray-200 dark:border-gray-700">
								<div className="flex flex-wrap gap-2">
									<TooltipProvider>
										{missing.skills.map((skill, index) => {
											const key = `skill-${skill.id}`;
											const isAdding = addingItems.has(key);
											const isAdded = addedItems.has(key);
											return (
												<Tooltip key={index}>
													<TooltipTrigger asChild>
														<Badge
															variant="outline"
															className={`text-xs cursor-pointer transition-all select-none ${
																isAdded
																	? 'bg-green-50 text-green-700 border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-700'
																	: 'hover:bg-gray-50 hover:border-gray-400 dark:hover:bg-gray-800'
															}`}
															onClick={(e) => { e.stopPropagation(); handleAddSkill(skill); }}
														>
															{isAdding
																? <Loader2 className="h-3 w-3 animate-spin mr-1" />
																: isAdded
																	? <Check className="h-3 w-3 mr-1" />
																	: <Plus className="h-3 w-3 mr-1" />
															}
															{skill.name}
														</Badge>
													</TooltipTrigger>
													<TooltipContent>
														{isAdded
															? t('matching.skillsComparison.added')
															: t('matching.skillsComparison.addToProfileTooltip')
														}
													</TooltipContent>
												</Tooltip>
											);
										})}
									</TooltipProvider>
								</div>
							</div>
						</div>
					)}

					{missing.experiences.length > 0 && (
						<div className="space-y-3">
							<div className="flex items-center gap-2">
								<AlertTriangle className="h-4 w-4 text-gray-500 dark:text-gray-400" />
								<h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
									{t('matching.skillsComparison.notInProfileExperiences', { count: missing.experiences.length })}
								</h3>
							</div>
							<p className="text-xs text-gray-500 dark:text-gray-400 italic">
								{t('matching.skillsComparison.notInProfileHint')}
							</p>
							<div className="rounded-sm p-3 border border-gray-200 dark:border-gray-700">
								<div className="flex flex-wrap gap-2">
									<TooltipProvider>
										{missing.experiences.map((exp, index) => {
											const key = `exp-${exp.id}`;
											const isAdding = addingItems.has(key);
											const isAdded = addedItems.has(key);
											return (
												<Tooltip key={index}>
													<TooltipTrigger asChild>
														<Badge
															variant="outline"
															className={`text-xs cursor-pointer transition-all select-none flex items-center gap-1 ${
																isAdded
																	? 'bg-green-50 text-green-700 border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-700'
																	: 'hover:bg-gray-50 hover:border-gray-400 dark:hover:bg-gray-800'
															}`}
															onClick={(e) => { e.stopPropagation(); handleAddExperience(exp); }}
														>
															{isAdding
																? <Loader2 className="h-3 w-3 animate-spin" />
																: isAdded
																	? <Check className="h-3 w-3" />
																	: <Plus className="h-3 w-3" />
															}
															<span>{exp.name}</span>
															<span className="text-xs opacity-60">({exp.category})</span>
														</Badge>
													</TooltipTrigger>
													<TooltipContent>
														{isAdded
															? t('matching.skillsComparison.added')
															: t('matching.skillsComparison.addToProfileTooltip')
														}
													</TooltipContent>
												</Tooltip>
											);
										})}
									</TooltipProvider>
								</div>
							</div>
						</div>
					)}
				</div>
			)}

			{/* Matching Skills and Experiences - Side by Side */}
			{(shared.skills.length > 0 || shared.experiences.length > 0) && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{shared.skills.length > 0 && (
						<div className="space-y-3">
							<div className="flex items-center gap-2">
								<div className="w-2 h-2 bg-green-500 rounded-full"></div>
								<h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
									Matching Skills ({shared.skills.length})
								</h3>
							</div>
							<div className="space-y-2">
								{shared.skills.map((skillMatch, index) => (
									<div
										key={index}
										className="p-3 rounded-sm border border-gray-200"
									>
										<div className="flex items-start justify-between gap-3">
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2 mb-1">
													<Badge variant="outline" className="text-xs">
														Required
													</Badge>
													<span className="font-medium text-sm text-gray-900 dark:text-gray-100">
														{skillMatch.job_skill.name}
													</span>
												</div>
												<div className="flex items-center gap-2">
													<Badge variant="outline" className="text-xs">
														Has
													</Badge>
													<span className="text-sm text-gray-700 dark:text-gray-200">
														{skillMatch.candidate_skill.raw_name}
													</span>
												</div>
											</div>
											<div className="flex flex-col items-end gap-1">
												<Badge
													variant={skillMatch.match_type === 'high' ? 'success' : 'default'}
													className="capitalize text-xs"
												>
													{skillMatch.match_type}
												</Badge>
												<span className="text-xs text-gray-500 dark:text-gray-400">
													{Math.round(skillMatch.similarity * 100)}%
												</span>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{shared.experiences.length > 0 && (
						<div className="space-y-3">
							<div className="flex items-center gap-2">
								<div className="w-2 h-2 bg-purple-500 rounded-full"></div>
								<h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
									Matching Experience ({shared.experiences.length})
								</h3>
							</div>
							<div className="space-y-2">
								{shared.experiences.map((expMatch, index) => (
									<div
										key={index}
										className="p-3 rounded-sm border border-gray-200"
									>
										<div className="flex items-start justify-between gap-3">
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2 mb-1">
													<Badge variant="outline" className="capitalize text-xs">
														{expMatch.job_experience.category}
													</Badge>
													<span className="font-medium text-sm text-gray-900 dark:text-gray-100">
														{expMatch.job_experience.name}
													</span>
												</div>
												<div className="text-sm text-gray-700 dark:text-gray-200">
													{expMatch.candidate_experience.raw_text}
													{(expMatch.candidate_experience.start_date || expMatch.candidate_experience.end_date) && (
														<span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
															({expMatch.candidate_experience.start_date && new Date(expMatch.candidate_experience.start_date).getFullYear()}
															{expMatch.candidate_experience.start_date && expMatch.candidate_experience.end_date && '-'}
															{expMatch.candidate_experience.end_date ? new Date(expMatch.candidate_experience.end_date).getFullYear() : expMatch.candidate_experience.start_date && 'Present'})
														</span>
													)}
												</div>
											</div>
											<div className="flex flex-col items-end gap-1">
												<Badge
													variant={expMatch.match_type === 'high' ? 'success' : 'default'}
													className="capitalize text-xs"
												>
													{expMatch.match_type}
												</Badge>
												<span className="text-xs text-gray-500 dark:text-gray-400">
													{Math.round(expMatch.similarity * 100)}%
												</span>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}

			{/* Collapsible Additional Information */}
			{(extra.skills.length > 0 || extra.experiences.length > 0) && (
				<Accordion type="single" collapsible className="w-full">
					{extra.skills.length > 0 && (
						<AccordionItem value="extra-skills">
							<AccordionTrigger className="text-sm font-semibold text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100">
								Additional Skills ({extra.skills.length})
							</AccordionTrigger>
							<AccordionContent>
								<div className="bg-blue-50 rounded-sm p-3 border border-blue-200">
									<div className="flex flex-wrap gap-2">
										{extra.skills.map((skill, index) => (
											<Badge
												key={index}
												variant="outline"
												className="bg-white dark:bg-gray-800 text-blue-700 border-blue-300 text-xs"
											>
												{skill.raw_name}
											</Badge>
										))}
									</div>
								</div>
							</AccordionContent>
						</AccordionItem>
					)}
					{extra.experiences.length > 0 && (
						<AccordionItem value="extra-experiences">
							<AccordionTrigger className="text-sm font-semibold text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100">
								Additional Experience ({extra.experiences.length})
							</AccordionTrigger>
							<AccordionContent>
								<div className="bg-indigo-50 rounded-sm p-3 border border-indigo-200">
									<div className="flex flex-wrap gap-2">
										{extra.experiences.map((exp, index) => (
											<Badge
												key={index}
												variant="outline"
												className="bg-white dark:bg-gray-800 text-indigo-700 border-indigo-300 text-xs flex items-center gap-1"
											>
												<span>{exp.raw_text}</span>
												<span className="text-xs opacity-60">({exp.category})</span>
											</Badge>
										))}
									</div>
								</div>
							</AccordionContent>
						</AccordionItem>
					)}
				</Accordion>
			)}
		</div>
	);
};
